/**
 * Billing PSP client-config (TMMRSL-0015 / 0021 / 0022; per tenant since 0029) —
 * `GET /v1/reseller-admin/tenants/{slug}/billing/client-config` (bearer). Always sends
 * `?scope=tenant|platform`. Wire type is generated `ScopedBillingClientConfig`
 * (`BillingClientConfig` + echoed `scope`). An echoed
 * `scope` that does not match the requested one is `unavailable` (never a token on
 * the wrong account). `providerFields` is never read (other-PSP ids for the console).
 * The publishable key is a public value and is never logged.
 *
 * The reseller's own card (TMMRSL-0046, TMMAPI-0098) reads the reseller-wide
 * `GET /v1/reseller-admin/billing/client-config` (`getReseller`, no parameters): the merchant of
 * record's keys, so its echoed `scope` is always `platform`. `409 MERCHANT_OF_RECORD_UNSET`,
 * `503 PAY_API_UNAVAILABLE` or any other `ApiError` is `unavailable`.
 */
import type { ScopedBillingClientConfig as Wire } from '@/api/generated';
import { request } from './client';
import { tenantPath } from './tenantApi';
import { ApiError } from './errors';

export type BillingClientConfigScope = 'tenant' | 'platform';

export interface StripeClientConfig {
  provider: 'stripe';
  publishableKey: string;
  stripeAccount: string | null;
  environment: string;
  flow: string | null;
  scope?: BillingClientConfigScope;
}

export type BillingClientConfigResult =
  | { kind: 'stripe'; config: StripeClientConfig }
  | { kind: 'unsupported'; provider: string }
  | { kind: 'unavailable' };

export function normalizeBillingClientConfig(
  wire: Wire,
  requestedScope?: BillingClientConfigScope,
): BillingClientConfigResult {
  if (wire.provider !== 'stripe') {
    return { kind: 'unsupported', provider: wire.provider };
  }
  // `publishableKey` stays optional: a blank or absent key cannot start Stripe.js.
  const publishableKey = typeof wire.publishableKey === 'string' ? wire.publishableKey.trim() : '';
  if (publishableKey.length === 0) {
    return { kind: 'unavailable' };
  }
  if (
    requestedScope !== undefined &&
    (wire.scope === 'tenant' || wire.scope === 'platform') &&
    wire.scope !== requestedScope
  ) {
    return { kind: 'unavailable' };
  }
  const config: StripeClientConfig = {
    provider: 'stripe',
    publishableKey,
    stripeAccount:
      typeof wire.stripeAccount === 'string' && wire.stripeAccount.length > 0
        ? wire.stripeAccount
        : null,
    environment: wire.environment,
    flow: typeof wire.flow === 'string' ? wire.flow : null,
  };
  if (wire.scope === 'tenant' || wire.scope === 'platform') {
    config.scope = wire.scope;
  }
  return { kind: 'stripe', config };
}

export const billingClientConfigApi = {
  async get(slug: string, scope: BillingClientConfigScope): Promise<BillingClientConfigResult> {
    try {
      return normalizeBillingClientConfig(
        await request<Wire>('GET', tenantPath(slug, 'billing/client-config'), {
          query: { scope },
        }),
        scope,
      );
    } catch (error) {
      // PAY_API_UNAVAILABLE (details.code) or any ApiError → unavailable. tmm-reseller-admin keeps
      // tmm-api envelope codes under details.code; a 503 is still an ApiError.
      if (error instanceof ApiError) {
        return { kind: 'unavailable' };
      }
      throw error;
    }
  },

  /** The reseller-wide config for the reseller's own card — always the platform's keys. */
  async getReseller(): Promise<BillingClientConfigResult> {
    try {
      return normalizeBillingClientConfig(
        await request<Wire>('GET', '/v1/reseller-admin/billing/client-config'),
        'platform',
      );
    } catch (error) {
      if (error instanceof ApiError) {
        return { kind: 'unavailable' };
      }
      throw error;
    }
  },
};
